/**
 * "Szybkie dodawanie jedną linią" of "Moje zadania" (owner 2026-10-22 01:19): the end of the line may say when —
 * "dziś", "jutro", "pojutrze", a weekday ("pt", "poniedziałek": the next one after today), a date ("14.11",
 * "14.11.2027"; without a year this year or, when past, the next) and a time ("10:00", "o 9:30"). What is not
 * recognised stays in the title; the title is never left empty.
 */
export type QuickLine = { title: string; dueDay: string | null; dueTime: string | null };

const WEEKDAYS: Array<[number, string[]]> = [
  [1, ['pon', 'pn', 'poniedziałek', 'poniedzialek']],
  [2, ['wt', 'wto', 'wtorek']],
  [3, ['śr', 'sr', 'środa', 'sroda', 'środę', 'srode']],
  [4, ['czw', 'cz', 'czwartek']],
  [5, ['pt', 'pią', 'pia', 'piątek', 'piatek']],
  [6, ['sob', 'sobota', 'sobotę', 'sobote']],
  [0, ['nd', 'ndz', 'niedziela', 'niedzielę', 'niedziele']],
];
const WEEKDAY_SHORT = ['nd.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'];

const iso = (d: Date) => d.toISOString().slice(0, 10);
const utc = (day: string) => new Date(`${day}T00:00:00Z`);
export function addDays(day: string, n: number): string {
  const d = utc(day);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
}
const pad = (n: number) => String(n).padStart(2, '0');
/** A real calendar day, or null (31.02). */
function realDay(y: number, m: number, d: number): string | null {
  const day = `${y}-${pad(m)}-${pad(d)}`;
  return m >= 1 && m <= 12 && d >= 1 && iso(utc(day)) === day ? day : null;
}

function dayOf(token: string, today: string): string | null {
  const t = token.toLocaleLowerCase('pl-PL').replace(/[.,]$/, '');
  if (t === 'dziś' || t === 'dzis' || t === 'dzisiaj') return today;
  if (t === 'jutro') return addDays(today, 1);
  if (t === 'pojutrze') return addDays(today, 2);
  const weekday = WEEKDAYS.find(([, words]) => words.includes(t))?.[0];
  if (weekday !== undefined) {
    const ahead = (weekday - utc(today).getUTCDay() + 7) % 7 || 7;
    return addDays(today, ahead);
  }
  // "14.11." / "14.11.2027.": the closing dot of a sentence
  const m = /^(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?\.?$/.exec(token);
  if (!m) return null;
  const [d, mo] = [Number(m[1]), Number(m[2])];
  if (m[3]) return realDay(Number(m[3]), mo, d);
  const year = Number(today.slice(0, 4));
  const day = realDay(year, mo, d);
  if (!day) return null;
  return day >= today ? day : realDay(year + 1, mo, d);
}

function timeOf(token: string): string | null {
  const m = /^([01]?\d|2[0-3])[:.]([0-5]\d)$/.exec(token);
  return m ? `${pad(Number(m[1]))}:${m[2]}` : null;
}

const isWeekday = (token: string) => WEEKDAYS.some(([, words]) => words.includes(token.toLocaleLowerCase('pl-PL').replace(/[.,]$/, '')));
/** "9.05" can be a day (9 May) or a time: a time right after "o", a day word or a date ("o 9.05", "jutro 9.05", "14.11 9.30"). */
const DOT_TIME = /^([01]?\d|2[0-3])\.([0-5]\d)$/;

export function parseQuickLine(text: string, today: string): QuickLine {
  const words = text.trim().split(/\s+/).filter(Boolean);
  let dueDay: string | null = null;
  let dueTime: string | null = null;
  let end = words.length;
  // from the end: at most one day and one time, in either order; "o" before a time and "w"/"we" before a weekday go with them
  while (end > 1) {
    const token = words[end - 1]!;
    const previous = words[end - 2]!;
    const timeAfterDay = dueTime === null && DOT_TIME.test(token) && (previous.toLowerCase() === 'o' || dayOf(previous, today) !== null);
    const day: string | null = dueDay === null && !timeAfterDay ? dayOf(token, today) : null;
    if (day) {
      dueDay = day;
      end--;
      if (end > 1 && isWeekday(token) && ['w', 'we'].includes(words[end - 1]!.toLowerCase())) end--;
      continue;
    }
    const time: string | null = dueTime === null ? timeOf(token) : null;
    if (time) {
      dueTime = time;
      end--;
      if (end > 1 && words[end - 1]!.toLowerCase() === 'o') end--;
      continue;
    }
    break;
  }
  // only a day (and a time) and nothing to do: the whole line is the title ("Jutro 10:00", "Piątek", "W piątek")
  const first = words[0]?.toLowerCase() ?? '';
  if (end === 1 && (dayOf(words[0]!, today) !== null || (dueDay !== null && (first === 'w' || first === 'we')))) return { title: words.join(' '), dueDay: null, dueTime: null };
  if (dueTime && !dueDay) dueDay = today;
  return { title: words.slice(0, end).join(' '), dueDay, dueTime };
}

/** "dziś", "jutro (sob. 24.10), 10:00", "sob. 14.11.2026", "bez daty": the preview under the line. */
export function whenText(dueDay: string | null, dueTime: string | null, today: string): string {
  if (!dueDay) return 'bez daty';
  const [y, m, d] = dueDay.split('-');
  const short = `${WEEKDAY_SHORT[utc(dueDay).getUTCDay()]} ${d}.${m}`;
  const day = dueDay === today ? 'dziś' : dueDay === addDays(today, 1) ? `jutro (${short})` : `${short}.${y}`;
  return dueTime ? `${day}, ${dueTime}` : day;
}
